import { Component, Input, OnInit } from '@angular/core';
import { Reservation } from "@app/models/micro/reservation.model";
import { FlightSummary } from "@app/models/micro/flight-summary.model";
import { GlobalsService } from "@services/micro/globals.service";
import {
  calculateTimeFlight,
  checkSegmentIsAffected,
  getSabreArrivalDate,
  getSabreDepartureDate,
  getSegmentArrivalDate,
  getSegmentDepartureDate,
  isSegmentDelayedOrEarly,
} from '@app/utils/functions/srIrops';
import { IropsService } from '@app/services/micro/irops.service';
/**
 * Componente que muestra el itinerario de vuelo, incluyendo aeropuertos de salida y llegada,
 * duración del vuelo, conexiones y horarios.
 */
@Component({
    selector: 'app-am-flight-itinerary',
    templateUrl: './am-flight-itinerary.component.html',
    styleUrls: ['./am-flight-itinerary.component.scss'],
    standalone: false
})
export class AmFlightItineraryComponent implements OnInit {
  /** Modelo de segmentos de vuelo */
  @Input('segments') segmentsModel: any;
  /** Indica si se debe mostrar la duración del vuelo */
  @Input() showDuration: boolean = true;
  /** Indica si se debe aplicar el tema oscuro */
  @Input() isDarkTheme: boolean = false;
  /** Indica si el vuelo es de tipo "rb" */
  @Input() isrb: boolean = false;
  /** Indica si el diseño debe ser grande */
  @Input() islarge: boolean = false;
  @Input() isOnBoarding:boolean = false;
  /** Indica si se muentra terminal y tiempo de vuelo */
  @Input() cterminal: boolean = true;

  @Input() statusIrops: any;
  @Input() segModify: any;

  /** Modelo que contiene el resumen del vuelo */
  flightSummary: FlightSummary;
  /** Objeto de la reservación actual */
  reservation: Reservation;
  /** Lista de segmentos de vuelo */
  segments: Array<any> = null;
  /** Indica si el vuelo es directo (sin escalas) */
  isNonStop: boolean = true;
  /** Lista de aeropuertos donde hay conexiones */
  hasConnect: any;
  /** Cadenas de texto con los aeropuertos de conexión */
  connections: string = '';
  /** Tiempo de conexión entre vuelos */
  timeConnecting;
  /** Duración total del vuelo en horas y minutos */
  time;
  /** Primer segmento del itinerario de vuelo */
  firstSegment: any = null;
  /** Último segmento del itinerario de vuelo */
  lastSegment: any = null;
  /** Código IATA del aeropuerto de salida */
  departureAirport: string;
  /** Código IATA del aeropuerto de llegada */
  arrivalAirport: string;
  /** Terminal de embarque del aeropuerto de salida */
  boardingTerminal: string;
  /** Fecha y hora de salida del vuelo */
  departureTime: string | Date;
  /** Fecha y hora de llegada del vuelo */
  arrivalTime: string | Date;
  /* primer segment afectado */
  afectedFirstSeg: any;
  /* ultimo segment afectado */
  afectedLastSeg: any;
  /* Indica si está afectado el primer segment */
  isAfectedFirstSeg: boolean;
  /* Indica si está afectado el ultimo segment */
  isAfectedLastSeg: boolean;

  /** Fecha y hora de salida del vuelo Original */
  departureOriginal: string | Date;
  /** Fecha y hora de llegada del vuelo Original */
  arrivalOriginal: string | Date;

  constructor(private global: GlobalsService, private _iropsServMicro:IropsService ) { }

  ngOnInit(): void {
    this.reservation = this.global.getReservation();
    this.segments = this.segmentsModel?.segments || this.segmentsModel;
    this.boardingTerminal = this.segments[0]?.departureTerminal ? "T"+this.segments[0]?.departureTerminal: '';
    this.isNonStop = this.segmentsModel.legType === 'NON-STOP';
    this.connections = this.connectionCities;

    this.firstSegment = this.segments[0];
    const last = this.segments.length;
    this.lastSegment = this.segments[last - 1];
    this.afectedFirstSeg = checkSegmentIsAffected(this.firstSegment);
    this.afectedLastSeg = checkSegmentIsAffected(this.lastSegment);

    this.departureAirport = this.getSegmentAirport(this.firstSegment, 'departure');
    this.arrivalAirport = this.getSegmentAirport(this.lastSegment, 'arrival');

    this.isAfectedFirstSeg = isSegmentDelayedOrEarly(this.firstSegment?.segment || this.firstSegment);
    this.isAfectedLastSeg = isSegmentDelayedOrEarly(this.lastSegment?.segment || this.lastSegment);

    const firstSeg = this.firstSegment?.segment || this.firstSegment;
    const lastSeg = this.lastSegment?.segment || this.lastSegment;
    const hasIropsAffectation = this.isAfectedFirstSeg || this.isAfectedLastSeg;

    // Horarios actuales (Flight Status si hay afectación)
    const depTime = getSegmentDepartureDate(firstSeg);
    const arrTime = getSegmentArrivalDate(lastSeg);

    this.departureTime = depTime;
    this.arrivalTime = arrTime;
    
    this.departureOriginal = getSabreDepartureDate(firstSeg) as any;
    this.arrivalOriginal = getSabreArrivalDate(lastSeg) as any;

    if (hasIropsAffectation) {
      const duration = calculateTimeFlight(
        getSabreDepartureDate(firstSeg),
        getSabreArrivalDate(lastSeg)
      );
      this.time = this.formatDuration(duration);

      if (this.hasConnect.length == 1) {
        this.timeConnecting = calculateTimeFlight(
          getSegmentArrivalDate(firstSeg),
          getSegmentDepartureDate(lastSeg)
        );
      }
    } else {
      this.time = this.getLegacyDuration();
      if (this.hasConnect.length == 1) {
        this.timeConnecting = calculateTimeFlight(
          firstSeg?.arrivalDateTime,
          lastSeg?.departureDateTime
        );
      }
    }
  }

  private getLegacyDuration(): string {
    const totalMinutesIrops = this._iropsServMicro.flightSelect?.totalFlightTimeInMinutes;
    const totalFlightTimeInHours = Number((this.segmentsModel.totalFlightTimeInMinutes / 60).toFixed(1));
    const hoursIropsMicro = Math.floor(totalMinutesIrops / 60);
    const minutesIropsMicro = totalMinutesIrops % 60;
    const hours = Math.floor(totalFlightTimeInHours);
    const minutes = Number(this.segmentsModel.totalFlightTimeInMinutes) % 60;

    return hours
      ? `${hours ? hours + ' h' : ''} ${minutes ? minutes + ' m' : ''}`
      : `${hoursIropsMicro ? hoursIropsMicro + ' h' : ''} ${minutesIropsMicro ? minutesIropsMicro + ' m' : ''}`;
  }

  private formatDuration(duration: { hours: number; minutes: number }): string {
    const { hours, minutes } = duration || { hours: 0, minutes: 0 };
    if (!hours && !minutes) {
      return this.getLegacyDuration().trim();
    }
    return `${hours ? hours + ' h' : ''} ${minutes ? minutes + ' m' : ''}`.trim();
  }
  /**
   * Codigo IATA de salida o llegada de un item de segmento, Segment o objeto con `.segment`
   */
  private getSegmentAirport(seg: any, kind: 'departure' | 'arrival'): string {
    if (!seg) {
      return '';
    }
    if (kind === 'departure') {
      return seg.departureAirport || seg.segment?.departureAirport || '';
    }
    return seg.arrivalAirport || seg.segment?.arrivalAirport || '';
  }

  /**
   * Aeropuertos de escala salida de cada tramo tras el primero, sin duplicar IATA consecutivos
   */
  private get connectionCities(): string {
    if (!this.segments?.length) {
      this.hasConnect = [];
      return '';
    }
    const layovers: string[] = [];
    for (let j = 1; j < this.segments.length; j++) {
      const dep = this.getSegmentAirport(this.segments[j], 'departure');
      if (dep && dep !== layovers[layovers.length - 1]) {
        layovers.push(dep);
      }
    }
    this.hasConnect = layovers;
    return layovers.join(', ') ?? '';
  }


  get classObjectSeperatatorLarge(): { [key: string]: boolean } {
    return {
      'AMFlightInfo-seperatator-large': this.islarge,
      'AMFlightInfo-seperatator': !this.islarge,
      [this.textColorClass]: true
    };
  }

  get classObjectSeperatatorLineLarge(): { [key: string]: boolean } {
    return {
      'AMFlightInfo-seperatator-line-large': this.islarge,
      'AMFlightInfo-seperatator': !this.islarge,
      [this.textColorClass]: true
    };
  }

  get classObjectAMFlightInfoTxtRigth(): { [key: string]: boolean } {
    return {
      'AMFlightInfo-txt-right': this.islarge,
      [this.textColorClass]: true
    };
  }

  get textColorClass(): string {
    if(this.isAfectedFirstSeg){
      if (this.segModify && this.statusIrops === 'Delayed') {
        return 'text-yellow';
      } else if (this.segModify && this.statusIrops === 'Early') {
        return 'text-green';
      }
    }
    return 'text-white';
  }


  get classObjectAMFlightInfoLine(): { [key: string]: boolean } {
    if(this.isAfectedFirstSeg) {
      if (this.segModify && this.statusIrops === 'Delayed') {
        return {
        'AMFlightInfo-line--dark': this.isDarkTheme,
        'AMFlightInfo-line--rb-yellow': this.isrb,
        'AMFlightInfo-line': !this.isrb
        }
      } else if (this.segModify && this.statusIrops === 'Early') {
        return {
          'AMFlightInfo-line--dark': this.isDarkTheme,
          'AMFlightInfo-line--rb-green': this.isrb,
          'AMFlightInfo-line': !this.isrb
          }
      }
    }
    return {
      'AMFlightInfo-line--dark': this.isDarkTheme,
      'AMFlightInfo-line--rb': this.isrb,
      'AMFlightInfo-line': !this.isrb
      }
    }

    get classObjectAMFlightCircleConexion(): { [key: string]: boolean } {
      if(this.isAfectedFirstSeg) {
        if (this.segModify && this.statusIrops === 'Delayed') {
          return {
          'AMFlightInfo-circle-conexion--dark': this.isDarkTheme,
          'AMFlightInfo-circle-conexion--rb-yellow': this.isrb,
          'AMFlightInfo-circle-conexion': !this.isrb
          }
        } else if (this.segModify && this.statusIrops === 'Early') {
          return {
            'AMFlightInfo-circle-conexion--dark': this.isDarkTheme,
            'AMFlightInfo-circle-conexion--rb-green': this.isrb,
            'AMFlightInfo-circle-conexion': !this.isrb
            }
        }
      }
      return {
        'AMFlightInfo-circle-conexion--dark': this.isDarkTheme,
        'AMFlightInfo-circle-conexion--rb': this.isrb,
        'AMFlightInfo-circle-conexion': !this.isrb
        }
      }

      get classObjectAMFlightCircleDot(): { [key: string]: boolean } {
        if(this.isAfectedFirstSeg) {
          if (this.segModify && this.statusIrops === 'Delayed') {
            return {
            'AMFlightInfo-cirle-dot--dark': this.isDarkTheme,
            'AMFlightInfo-cirle-dot--rb-yellow': this.isrb,
            'AMFlightInfo-cirle-dot': !this.isrb
            }
          } else if (this.segModify && this.statusIrops === 'Early') {
            return {
              'AMFlightInfo-cirle-dot--dark': this.isDarkTheme,
              'AMFlightInfo-cirle-dot--rb-green': this.isrb,
              'AMFlightInfo-cirle-dot': !this.isrb
              }
          }
        }
        return {
          'AMFlightInfo-cirle-dot--dark': this.isDarkTheme,
          'AMFlightInfo-cirle-dot--rb': this.isrb,
          'AMFlightInfo-cirle-dot': !this.isrb
          }
        }

}
